import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Alert, AlertDescription } from '../ui/alert';
import { Plus, Check, Trash2, Pencil, AlertTriangle, Clock, CheckCircle2, Tags, X } from 'lucide-react';
import { overlayClose } from '../../lib/overlayClose';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (v) => `R$ ${Number(v || 0).toFixed(2)}`;

const PAYMENT_METHODS = [
  { value: 'dinheiro', label: 'Dinheiro' },
  { value: 'pix', label: 'PIX' },
  { value: 'pix_maquininha', label: 'PIX na Maquininha' },
  { value: 'cartao', label: 'Cartão' },
  { value: 'boleto', label: 'Boleto' },
  { value: 'transferencia', label: 'Transferência' },
];

const emptyForm = () => ({
  description: '', category: '', amount: '',
  due_date: new Date().toISOString().substring(0, 10),
  responsible: '', notes: '',
});

export const FixedExpensesList = () => {
  const [expenses, setExpenses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [payTarget, setPayTarget] = useState(null);
  const [payMethod, setPayMethod] = useState('dinheiro');
  const [showCategories, setShowCategories] = useState(false);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterPeriod, setFilterPeriod] = useState('all');
  const [customRange, setCustomRange] = useState({ start: '', end: '' });

  const load = useCallback(async () => {
    try {
      const [eRes, cRes] = await Promise.all([
        axios.get(`${API}/financial/expenses`, { withCredentials: true }),
        axios.get(`${API}/financial/expense-categories`, { withCredentials: true }),
      ]);
      setExpenses(eRes.data);
      setCategories(cRes.data);
    } catch (err) { console.error(err); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openNew = () => { setForm(emptyForm()); setEditId(null); setError(''); setShowForm(true); };
  const openEdit = (ex) => {
    setForm({
      description: ex.description, category: ex.category, amount: String(ex.amount),
      due_date: new Date(ex.due_date).toISOString().substring(0, 10),
      responsible: ex.responsible || '', notes: ex.notes || '',
    });
    setEditId(ex.id); setError(''); setShowForm(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.description || !form.category || !form.amount) { setError('Preencha descrição, categoria e valor'); return; }
    setLoading(true); setError('');
    try {
      const payload = {
        description: form.description,
        category: form.category,
        amount: parseFloat(form.amount),
        due_date: new Date(form.due_date).toISOString(),
        responsible: form.responsible || null,
        notes: form.notes || null,
      };
      if (editId) await axios.put(`${API}/financial/expenses/${editId}`, payload, { withCredentials: true });
      else await axios.post(`${API}/financial/expenses`, payload, { withCredentials: true });
      setShowForm(false); setEditId(null); setForm(emptyForm());
      load();
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao salvar despesa');
    } finally { setLoading(false); }
  };

  const confirmPay = async () => {
    if (!payTarget) return;
    setLoading(true);
    try {
      await axios.post(`${API}/financial/expenses/${payTarget.id}/pay`, { payment_method: payMethod }, { withCredentials: true });
      setPayTarget(null); setPayMethod('dinheiro');
      load();
    } catch (err) { setError(err.response?.data?.detail || 'Erro ao pagar'); }
    finally { setLoading(false); }
  };

  const remove = async (id) => {
    if (!window.confirm('Excluir esta despesa?')) return;
    try { await axios.delete(`${API}/financial/expenses/${id}`, { withCredentials: true }); load(); }
    catch (err) { console.error(err); }
  };

  const totalPendente = expenses.filter(e => e.status === 'pendente').reduce((s, e) => s + e.amount, 0);
  const totalVencido = expenses.filter(e => e.status === 'vencido').reduce((s, e) => s + e.amount, 0);
  const totalPago = expenses.filter(e => e.status === 'pago').reduce((s, e) => s + e.amount, 0);

  const monthRange = (offset) => {
    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth() + offset, 1);
    const end = new Date(now.getFullYear(), now.getMonth() + offset + 1, 0);
    return { start, end };
  };
  const activeRange = () => {
    if (filterPeriod === 'current') return monthRange(0);
    if (filterPeriod === 'next') return monthRange(1);
    if (filterPeriod === 'custom') {
      return {
        start: customRange.start ? new Date(customRange.start + 'T00:00:00') : null,
        end: customRange.end ? new Date(customRange.end + 'T23:59:59') : null,
      };
    }
    return { start: null, end: null };
  };
  const inPeriod = (dueISO) => {
    if (filterPeriod === 'all') return true;
    if (!dueISO) return false;
    const d = new Date(dueISO);
    const { start, end } = activeRange();
    if (start && d < start) return false;
    if (end && d > end) return false;
    return true;
  };

  const visibleExpenses = expenses.filter(e =>
    (filterStatus === 'all' || e.status === filterStatus) &&
    (filterCategory === 'all' || e.category === filterCategory) &&
    inPeriod(e.due_date)
  );
  const filtersActive = filterStatus !== 'all' || filterCategory !== 'all' || filterPeriod !== 'all';

  const statusBadge = (status) => {
    if (status === 'vencido') return <span className="text-xs px-2 py-1 rounded bg-red-100 text-red-700 font-bold flex items-center gap-1 w-fit"><AlertTriangle size={12} />Vencido</span>;
    if (status === 'pago') return <span className="text-xs px-2 py-1 rounded bg-green-100 text-green-700 font-bold flex items-center gap-1 w-fit"><CheckCircle2 size={12} />Pago</span>;
    return <span className="text-xs px-2 py-1 rounded bg-yellow-100 text-yellow-700 font-bold flex items-center gap-1 w-fit"><Clock size={12} />Pendente</span>;
  };

  return (
    <div data-testid="fixed-expenses">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-white rounded-md border border-slate-200 p-4">
          <p className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500">Pendentes</p>
          <p className="text-2xl font-bold text-yellow-700 mt-1">{fmt(totalPendente)}</p>
        </div>
        <div className="bg-white rounded-md border border-slate-200 p-4">
          <p className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500">Vencidas</p>
          <p className="text-2xl font-bold text-red-600 mt-1">{fmt(totalVencido)}</p>
        </div>
        <div className="bg-white rounded-md border border-slate-200 p-4">
          <p className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500">Pagas</p>
          <p className="text-2xl font-bold text-green-600 mt-1">{fmt(totalPago)}</p>
        </div>
      </div>

      <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
        <div className="flex flex-wrap items-end gap-3" data-testid="expense-filters">
          <div className="space-y-1">
            <Label className="text-[10px] uppercase font-bold text-slate-500">Status</Label>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="h-9 w-40" data-testid="filter-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos os status</SelectItem>
                <SelectItem value="pendente">Pendente</SelectItem>
                <SelectItem value="vencido">Vencido</SelectItem>
                <SelectItem value="pago">Pago</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-[10px] uppercase font-bold text-slate-500">Categoria</Label>
            <Select value={filterCategory} onValueChange={setFilterCategory}>
              <SelectTrigger className="h-9 w-56" data-testid="filter-category"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas as categorias</SelectItem>
                {categories.map(c => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-[10px] uppercase font-bold text-slate-500">Período (vencimento)</Label>
            <Select value={filterPeriod} onValueChange={setFilterPeriod}>
              <SelectTrigger className="h-9 w-48" data-testid="filter-period"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos os períodos</SelectItem>
                <SelectItem value="current">Mês Atual</SelectItem>
                <SelectItem value="next">Próximo Mês</SelectItem>
                <SelectItem value="custom">Intervalo Personalizado</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {filterPeriod === 'custom' && (
            <>
              <div className="space-y-1">
                <Label className="text-[10px] uppercase font-bold text-slate-500">De</Label>
                <Input type="date" className="h-9 w-40" value={customRange.start} data-testid="filter-custom-start"
                  onChange={(e) => setCustomRange({ ...customRange, start: e.target.value })} />
              </div>
              <div className="space-y-1">
                <Label className="text-[10px] uppercase font-bold text-slate-500">Até</Label>
                <Input type="date" className="h-9 w-40" value={customRange.end} data-testid="filter-custom-end"
                  onChange={(e) => setCustomRange({ ...customRange, end: e.target.value })} />
              </div>
            </>
          )}
          {filtersActive && (
            <Button variant="ghost" className="h-9 text-slate-500" onClick={() => { setFilterStatus('all'); setFilterCategory('all'); setFilterPeriod('all'); setCustomRange({ start: '', end: '' }); }} data-testid="clear-filters">
              <X size={16} className="mr-1" /> Limpar filtros
            </Button>
          )}
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setShowCategories(true)} data-testid="manage-categories-btn">
            <Tags size={18} className="mr-2" /> Gerenciar Categorias
          </Button>
          <Button onClick={openNew} className="bg-blue-600 hover:bg-blue-700" data-testid="add-expense-btn">
            <Plus size={18} className="mr-2" /> Nova Despesa Fixa
          </Button>
        </div>
      </div>

      {showForm && (
        <div className="bg-white rounded-md border border-slate-200 p-6 mb-6">
          <h2 className="text-2xl font-bold text-slate-900 mb-4">{editId ? 'Editar Despesa' : 'Nova Despesa Fixa'}</h2>
          <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2 md:col-span-2">
              <Label>Descrição *</Label>
              <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="expense-description" required />
            </div>
            <div className="space-y-2">
              <Label>Categoria *</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger data-testid="expense-category"><SelectValue placeholder="Selecione a categoria" /></SelectTrigger>
                <SelectContent>
                  {categories.map(c => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Valor (R$) *</Label>
              <Input type="number" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="expense-amount" required />
            </div>
            <div className="space-y-2">
              <Label>Data de Vencimento *</Label>
              <Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} data-testid="expense-due-date" required />
            </div>
            <div className="space-y-2">
              <Label>Responsável</Label>
              <Input value={form.responsible} onChange={(e) => setForm({ ...form, responsible: e.target.value })} placeholder="Quem controla essa despesa" />
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label>Observação</Label>
              <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </div>
            {error && <div className="md:col-span-2"><Alert variant="destructive"><AlertDescription>{error}</AlertDescription></Alert></div>}
            <div className="md:col-span-2 flex gap-2">
              <Button type="submit" disabled={loading} className="bg-blue-600 hover:bg-blue-700" data-testid="save-expense-btn">{loading ? 'Salvando...' : 'Salvar'}</Button>
              <Button type="button" variant="outline" onClick={() => { setShowForm(false); setEditId(null); }}>Cancelar</Button>
            </div>
          </form>
        </div>
      )}

      <div className="bg-white rounded-md border border-slate-200 overflow-hidden">
        <table className="w-full">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Vencimento</th>
              <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Descrição</th>
              <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Categoria</th>
              <th className="px-4 py-3 text-right text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Valor</th>
              <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Status</th>
              <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Ações</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200">
            {visibleExpenses.length === 0 && <tr><td colSpan={6} className="text-center py-8 text-slate-400">{filtersActive ? 'Nenhuma despesa para os filtros selecionados' : 'Nenhuma despesa cadastrada'}</td></tr>}
            {visibleExpenses.map((ex) => {
              const done = ex.status === 'pago';
              return (
                <tr key={ex.id} className={done ? 'bg-green-50' : 'hover:bg-slate-50'} data-testid={`expense-row-${ex.id}`}>
                  <td className="px-4 py-3 text-sm">{new Date(ex.due_date).toLocaleDateString('pt-BR')}</td>
                  <td className="px-4 py-3 text-sm font-medium text-slate-900">{ex.description}
                    {ex.responsible && <div className="text-xs text-slate-400">{ex.responsible}</div>}
                  </td>
                  <td className="px-4 py-3 text-xs"><span className="px-2 py-1 rounded bg-slate-100 text-slate-700 font-medium">{ex.category}</span></td>
                  <td className="px-4 py-3 text-right font-bold text-slate-900">{fmt(ex.amount)}</td>
                  <td className="px-4 py-3">{statusBadge(ex.status)}</td>
                  <td className="px-4 py-3">
                    <div className="flex gap-3 items-center">
                      {!done && (
                        <button onClick={() => { setPayTarget(ex); setPayMethod('dinheiro'); }} className="text-green-600 hover:text-green-700 font-medium text-sm flex items-center gap-1" data-testid={`pay-expense-${ex.id}`}>
                          <Check size={16} /> Pagar
                        </button>
                      )}
                      <button onClick={() => openEdit(ex)} className="text-slate-500 hover:text-slate-700" data-testid={`edit-expense-${ex.id}`}><Pencil size={16} /></button>
                      <button onClick={() => remove(ex.id)} className="text-red-600 hover:text-red-700"><Trash2 size={16} /></button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Modal pagar */}
      {payTarget && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" {...overlayClose(() => setPayTarget(null))}>
          <div className="bg-white rounded-lg p-6 w-96" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-xl font-bold text-slate-900 mb-4">Pagar Despesa</h3>
            <div className="bg-slate-50 border border-slate-200 rounded p-3 text-sm mb-4">
              <div className="flex justify-between"><span className="text-slate-500">Descrição</span><span className="font-bold text-slate-900">{payTarget.description}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Valor</span><span className="font-bold text-red-600">{fmt(payTarget.amount)}</span></div>
            </div>
            <div className="space-y-2 mb-4">
              <Label>Forma de Pagamento *</Label>
              <Select value={payMethod} onValueChange={setPayMethod}>
                <SelectTrigger data-testid="expense-pay-method"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PAYMENT_METHODS.map(m => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setPayTarget(null)} className="flex-1">Cancelar</Button>
              <Button onClick={confirmPay} disabled={loading} className="flex-1 bg-blue-600 hover:bg-blue-700" data-testid="confirm-pay-expense">{loading ? 'Processando...' : 'Confirmar'}</Button>
            </div>
          </div>
        </div>
      )}

      {showCategories && <CategoriesManager categories={categories} onClose={() => setShowCategories(false)} onChange={load} />}
    </div>
  );
};

const CategoriesManager = ({ categories, onClose, onChange }) => {
  const [newName, setNewName] = useState('');
  const [error, setError] = useState('');
  const [all, setAll] = useState([]);

  const reload = useCallback(async () => {
    const res = await axios.get(`${API}/financial/expense-categories?include_inactive=true`, { withCredentials: true });
    setAll(res.data);
  }, []);
  useEffect(() => { reload(); }, [reload]);

  const add = async () => {
    if (!newName.trim()) return;
    setError('');
    try {
      await axios.post(`${API}/financial/expense-categories`, { name: newName.trim() }, { withCredentials: true });
      setNewName(''); reload(); onChange();
    } catch (err) { setError(err.response?.data?.detail || 'Erro ao criar categoria'); }
  };

  const toggle = async (c) => {
    await axios.put(`${API}/financial/expense-categories/${c.id}`, { active: !c.active }, { withCredentials: true });
    reload(); onChange();
  };

  const rename = async (c) => {
    const name = window.prompt('Novo nome da categoria', c.name);
    if (!name || !name.trim()) return;
    await axios.put(`${API}/financial/expense-categories/${c.id}`, { name: name.trim() }, { withCredentials: true });
    reload(); onChange();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" {...overlayClose(onClose)}>
      <div className="bg-white rounded-lg p-6 w-[32rem] max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="categories-manager">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl font-bold text-slate-900">Categorias de Despesa</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X size={20} /></button>
        </div>
        <div className="flex gap-2 mb-4">
          <Input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="Nova categoria" data-testid="new-category-input"
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add(); } }} />
          <Button onClick={add} className="bg-blue-600 hover:bg-blue-700" data-testid="add-category-btn"><Plus size={16} /></Button>
        </div>
        {error && <Alert variant="destructive" className="mb-3"><AlertDescription>{error}</AlertDescription></Alert>}
        <div className="border border-slate-200 rounded divide-y divide-slate-100">
          {all.map(c => (
            <div key={c.id} className="flex items-center justify-between px-3 py-2" data-testid={`category-item-${c.id}`}>
              <span className={`text-sm ${c.active ? 'text-slate-900' : 'text-slate-400 line-through'}`}>{c.name}{c.is_default && <span className="ml-2 text-[10px] text-slate-400">(padrão)</span>}</span>
              <div className="flex gap-3">
                <button onClick={() => rename(c)} className="text-slate-500 hover:text-slate-700 text-xs"><Pencil size={14} /></button>
                <button onClick={() => toggle(c)} className={`text-xs font-bold ${c.active ? 'text-amber-600' : 'text-green-600'}`}>
                  {c.active ? 'Desativar' : 'Ativar'}
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
